/**
 * Which credit pool a top-up is bought for, when the buyer has more than one (#724).
 *
 * A user can spend from their own pool and from every team they are an active member
 * of, so a purchase now has to name one. What they may BUY for is a smaller set than
 * what they may spend from — a team pool takes owner/billing-manager rights — which is
 * why the choice is filtered rather than simply mirroring the pool list.
 *
 * Deliberately free of imports so it can be unit-tested directly. The shape below is
 * the minimum this module consumes; the wire type `AiCreditPurchasePool` satisfies it
 * structurally.
 */

/** The parts of a pool needed to offer it, choose between them, and name one. */
export interface ChoosablePool {
    /** 'user' for the buyer's own pool, 'team' for a shared one. */
    ownerType: string;
    /** The team's name; null for the personal pool, which is named here instead. */
    name?: string | null;
    /** True on the one pool the user's AI usage is currently billed to. */
    isCurrent?: boolean;
    /** Whether this user may buy top-up credits for this pool (#76, #465). */
    canPurchase?: boolean;
}

/**
 * The pools this buyer may actually top up, in the order the server sent them — the
 * order spend falls through pools, so re-sorting would throw away what the order
 * explains.
 *
 * Pools they may not buy for are dropped rather than disabled: an option that cannot
 * be picked is a question the buyer has to answer about somebody else's rights, and
 * the server would refuse the purchase anyway.
 */
export function purchasablePools<T extends ChoosablePool>(pools: T[] | null | undefined): T[] {
    return (pools || []).filter(pool => !!pool && !!pool.canPurchase);
}

/**
 * Whether to draw the "Add credits to" row at all.
 *
 * Two separate reasons to draw it, and keying only on the first was a money bug
 * (#724). The obvious reason is a real choice: more than one pool the buyer may top
 * up. The other is that the single pool they may buy for is not the pool being
 * charged — a plain member of a funded team can buy for their own pool only, while
 * the balance panel directly above says their AI usage comes out of the team's. With
 * the row hidden, live Buy buttons sent real money to a pool the server will not spend
 * from at all while the team pool has credits, and nothing on screen said so. The
 * select then carries one option, which is fine: naming the destination is the point,
 * and the one-line answer costs less than a silent mismatch.
 *
 * One purchasable pool that IS the billed pool — the overwhelmingly common case, an
 * individual with no team — is no choice and no question, so the panel stays exactly
 * as it always looked. Zero purchasable pools is likewise nothing to name.
 *
 * `purchasingAvailable` is the deployment having a credit pack catalogue at all. The
 * pools belong to the buyer, not to the catalogue, so the server sends them either
 * way; keyed on the pools alone this row named a destination for a purchase that
 * cannot be started, above an empty pack area (#746). It is a required argument
 * rather than one defaulting to true because a caller that hasn't answered it hasn't
 * decided, and the default would hide that.
 */
export function shouldOfferPoolChoice(
    pools: ChoosablePool[] | null | undefined,
    purchasingAvailable: boolean,
): boolean {
    if (!purchasingAvailable) return false;

    const target = defaultPurchasePool(pools);
    if (!target) return false;

    return purchasablePools(pools).length > 1 || !target.isCurrent;
}

/**
 * The pool the selector starts on: the one the buyer's AI usage is currently billed
 * to, because topping up the pool you are spending from is what someone who came here
 * to buy credits almost always means.
 *
 * When they may not buy for that pool — a plain member of a team whose pool is being
 * billed — the first pool they CAN buy for takes over. Never a pool they cannot buy
 * for: that would offer a purchase the server is bound to refuse.
 */
export function defaultPurchasePool<T extends ChoosablePool>(pools: T[] | null | undefined): T | null {
    const purchasable = purchasablePools(pools);
    if (purchasable.length === 0) return null;

    const billed = purchasable.filter(pool => !!pool.isCurrent);

    return billed.length > 0 ? billed[0] : purchasable[0];
}

/**
 * How a pool reads in the selector. The personal pool arrives with no name — naming
 * it is the client's job — and a team with a blank name still has to say something,
 * so neither case can fall through to an empty option.
 *
 * The billed pool is marked, because "which pool am I topping up" and "which pool is
 * my usage coming out of" are the two questions the buyer has, and answering them in
 * one line is the whole point of #724.
 */
export function purchasePoolLabel(pool: ChoosablePool | null | undefined): string {
    if (!pool) return '';

    const teamName = (pool.name || '').trim();
    const name = pool.ownerType === 'team'
        ? (teamName || 'Your team')
        : 'Your personal credits';

    return pool.isCurrent ? name + ' — currently billed' : name;
}
